// VoiLog Web版 Firebase Remote Config 薄ラッパ。
//
// 2026-06-18 DEC-049 更新: 無料プランの閲覧件数（直近 N 件）を RemoteConfig で
// 動的制御できるようにする。Flutter 側 RemoteConfigService と同名キー
// `free_timeline_view_limit`（NUMBER, 既定2, 範囲 1..50）を参照する。
//
// 取得失敗・未対応環境（SSR/テスト等）では必ず既定値（FREE_TIMELINE_LIMIT）に倒し、
// 画面描画を止めない（オフライン/フェッチ失敗でも壊れない設計）。

import { useEffect, useState } from 'react';
import {
  getRemoteConfig,
  fetchAndActivate,
  getBoolean,
  getNumber,
  type RemoteConfig,
} from 'firebase/remote-config';
import { app } from './firebase';
import { FREE_TIMELINE_LIMIT } from './plan';

export const FREE_TIMELINE_VIEW_LIMIT_KEY = 'free_timeline_view_limit';
const VIEW_LIMIT_MIN = 1;
const VIEW_LIMIT_MAX = 50;

// S-063-15 / DEC-062: カレンダー取込「生テキスト保管」方式の master switch（BOOLEAN・既定 false）。
//   true = 新生テキスト経路（貼付→日付分割→calendar_days 保存）、false = 旧構造化経路（DEC-061）。
//   内部テストで安全確認できるまで既定 false。旧経路への即 rollback 用（GATE-C・並存）。
export const CALENDAR_RAWTEXT_ENABLED_KEY = 'calendar_rawtext_enabled';

// S-063-17/19 / DEC-062: 過去議事録の遡及照合（要件 B）の起動可否（BOOLEAN・既定 false）。
//   true = 生テキスト保存後に matchCalendarEventsForApproval を起動し自動/要確認の二分プレビューへ。
//   false = 遡及照合を起動しない（保存のみ）。サーバ側 config/calendar.retroEnabled と対になる撤退弁。
export const CALENDAR_RETRO_ENABLED_KEY = 'calendar_retro_enabled';

let _rc: RemoteConfig | null = null;
let _fetchStarted = false;

// Remote Config インスタンスを遅延初期化する。未対応環境では null を返す。
function getRC(): RemoteConfig | null {
  if (_rc) return _rc;
  try {
    const rc = getRemoteConfig(app);
    rc.defaultConfig = {
      [FREE_TIMELINE_VIEW_LIMIT_KEY]: FREE_TIMELINE_LIMIT,
      [CALENDAR_RAWTEXT_ENABLED_KEY]: false,
      [CALENDAR_RETRO_ENABLED_KEY]: false,
    };
    // 1時間キャッシュ（過剰フェッチ防止）。
    rc.settings.minimumFetchIntervalMillis = 60 * 60 * 1000;
    _rc = rc;
    return _rc;
  } catch {
    return null;
  }
}

// 最新値を取りに行く（fire-and-forget・1回だけ起動）。失敗は呑む。
function ensureFetch(rc: RemoteConfig): Promise<void> {
  if (_fetchStarted) return Promise.resolve();
  _fetchStarted = true;
  return fetchAndActivate(rc)
    .then(() => undefined)
    .catch(() => undefined);
}

// 現在の閲覧件数（直近 N 件）。範囲外・取得不能は既定（FREE_TIMELINE_LIMIT）に倒す。
export function getFreeTimelineViewLimit(): number {
  try {
    const rc = getRC();
    if (!rc) return FREE_TIMELINE_LIMIT;
    const n = getNumber(rc, FREE_TIMELINE_VIEW_LIMIT_KEY);
    if (!Number.isFinite(n) || n < VIEW_LIMIT_MIN || n > VIEW_LIMIT_MAX) {
      return FREE_TIMELINE_LIMIT;
    }
    return Math.floor(n);
  } catch {
    return FREE_TIMELINE_LIMIT;
  }
}

// React フック: 既定値で即描画し、フェッチ完了後に最新値へ差し替える。
export function useFreeTimelineViewLimit(): number {
  const [limit, setLimit] = useState<number>(() => getFreeTimelineViewLimit());
  useEffect(() => {
    let cancelled = false;
    const rc = getRC();
    if (!rc) return;
    ensureFetch(rc).then(() => {
      if (!cancelled) setLimit(getFreeTimelineViewLimit());
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return limit;
}

// 生テキスト取込経路が有効か。取得不能・未対応環境では既定 false（旧構造化経路に倒す）。
export function getCalendarRawtextEnabled(): boolean {
  try {
    const rc = getRC();
    if (!rc) return false;
    return getBoolean(rc, CALENDAR_RAWTEXT_ENABLED_KEY);
  } catch {
    return false;
  }
}

// React フック: 既定 false で即描画し、フェッチ完了後に最新値へ差し替える。
export function useCalendarRawtextEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => getCalendarRawtextEnabled());
  useEffect(() => {
    let cancelled = false;
    const rc = getRC();
    if (!rc) return;
    ensureFetch(rc).then(() => {
      if (!cancelled) setEnabled(getCalendarRawtextEnabled());
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return enabled;
}

// 遡及照合を起動してよいか。取得不能・未対応環境では既定 false（保存のみ＝安全側）。
export function getCalendarRetroEnabled(): boolean {
  try {
    const rc = getRC();
    if (!rc) return false;
    return getBoolean(rc, CALENDAR_RETRO_ENABLED_KEY);
  } catch {
    return false;
  }
}

// React フック: 既定 false で即描画し、フェッチ完了後に最新値へ差し替える。
export function useCalendarRetroEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(() => getCalendarRetroEnabled());
  useEffect(() => {
    let cancelled = false;
    const rc = getRC();
    if (!rc) return;
    ensureFetch(rc).then(() => {
      if (!cancelled) setEnabled(getCalendarRetroEnabled());
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return enabled;
}
